import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  Modal,
  Button as AntButton,
  Input as AntInput,
  Pagination as AntPagination,
  Alert,
  Table,
} from 'antd';
import {
  Paper,
  Button as MuiButton,
  Typography,
} from '@mui/material';
import SpinLoader from 'components/Loader/SpinLoader';
import {
  handleArchiveInappropriateAlert,
  handleGetMoreInfoInappropriateAlert,
  handleListInappropriateAlert,
  handleListLengthInappropriateAlert,
} from 'store/logs/thunks';
import moment from 'moment-timezone';
import * as Sentry from '@sentry/react';

const Container = styled.div`
  padding: 20px;
`;

const SearchBar = styled(AntInput)`
  margin-bottom: 20px;
`;

const ListContainer = styled(Paper)`
  padding: 20px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
`;

const HighlightBox = styled.div`
  background-color: #fef3f3;
  padding: 15px;
  border-left: 4px solid #d9534f;
  margin: 16px 0;
`;

const InappropriateWordLog = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmingArchive, setConfirmingArchive] = useState(false);

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const dt = await handleListLengthInappropriateAlert();
      const words = await handleListInappropriateAlert((page - 1) * 100, 100);
      if (dt && dt.success) {
        const total = dt.data;
        if (!isNaN(total)) {
          setTotalPages(Math.ceil(total / 100));
        }
      }
      if (words && words.success) {
        setData(words.data);
      }
    } catch (error) {
      Sentry.captureException(`270 Error fetching inappropriate words: ${error}`);
      console.error(error);
    }
    setLoading(false);
  };

  const handleViewMore = async (item) => {
    try {
        setLoading(true)
      const response = await handleGetMoreInfoInappropriateAlert(item.id);
      if (response && response.success) {
        setSelectedItem(response.data);
        setModalVisible(true);
      }
    } catch (error) {
      Sentry.captureException(`271 Error fetching more info: ${error}`);
      console.error(error);
    } finally{
        setLoading(false)
    }
  };

  const handleArchive = async (id) => {
    try {
        setLoading(true)
      const data = await handleArchiveInappropriateAlert(id);
      if (data && data.success) {
        setData((k) => k.filter((item) => item.id !== id));
      }
      setModalVisible(false);
    } catch (error) {
      Sentry.captureException(`272 Error archiving item: ${error}`);
      console.error(error);
    } finally{
        setLoading(false)
    }
  };

  const columns = [
    {
      title: 'Word Used',
      dataIndex: 'word_used',
      key: 'word_used',
    },
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        `${record.userInfo.firstName} ${record.userInfo.lastName} (${record.userInfo.role})`
      )
    },
    {
      title: 'Class',
      key: 'class_name',
      render: (_, record) => record.meta_data.class_name,
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => record.meta_data.location,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Actions>
          <MuiButton
            variant="outlined"
            color="primary"
            onClick={() => handleViewMore(record)}
          >
            View More
          </MuiButton>
          <MuiButton
            variant="outlined"
            color="secondary"
            onClick={() => {
                setConfirmingArchive(true)
                setSelectedItem(record)
            }}
          >
            Archive
          </MuiButton>
        </Actions>
      ),
    },
  ];

  const filterData = useMemo(() => {
    return data.filter((item) => item?.word_used?.toLowerCase().includes(search?.toLowerCase()));
  }, [data, search]);

  return (
    <Container>
      <Typography variant="h5" gutterBottom>Inappropriate Words Log</Typography>
      <SearchBar
        placeholder="Search..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <ListContainer elevation={3}>
        <SpinLoader dataloader={loading} />
          <Table
            columns={columns}
            dataSource={filterData}
            rowKey="id"
            pagination={false}
          />
        <AntPagination
          current={page}
          total={totalPages * 100}
          pageSize={100}
          onChange={(p) => setPage(p)}
          showSizeChanger={false}
          style={{ marginTop: '20px', textAlign: 'center' }}
        />
      </ListContainer>

      <Modal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedItem && (
          <div>
            <Typography variant="h6" gutterBottom>🚫 Inappropriate Word Detail</Typography>

            <HighlightBox>
              <Typography variant="body1"><strong>Word:</strong> {selectedItem.word_used}</Typography>
              <Typography variant="body1"><strong>User:</strong> {selectedItem.userInfo.firstName} {selectedItem.userInfo.lastName} ({selectedItem.userInfo.role})</Typography>
              <Typography variant="body1"><strong>Class:</strong> {selectedItem.meta_data.class_name}</Typography>
              <Typography variant="body1"><strong>Location:</strong> {selectedItem.meta_data.location}</Typography>
              <Typography variant="body1"><strong>School Name:</strong> {selectedItem.orgInfo?.school_name || 'N/A'}</Typography>
              <Typography variant="body1"><strong>District:</strong> {selectedItem.orgInfo?.district_name || 'N/A'}</Typography>
              <Typography variant="body2" style={{ marginTop: '10px' }}><strong>Detected At:</strong> {moment(selectedItem.created_at).format('MMM DD, YYYY hh:mm A')}</Typography>
            </HighlightBox>

            {Array.isArray(selectedItem.texts) && selectedItem.texts.length > 0 && (
              <div>
                <Typography variant="subtitle1" gutterBottom>Contextual Sentences:</Typography>
                <ul>
                  {selectedItem.texts.map((sentence, idx) => (
                    <li key={idx}><Typography variant="body2">{sentence}</Typography></li>
                  ))}
                </ul>
              </div>
            )}

            <Actions style={{ marginTop: 20 }}>
              <AntButton danger onClick={() => setConfirmingArchive(true)}>Archive</AntButton>
              <AntButton onClick={() => setModalVisible(false)}>OK</AntButton>
            </Actions>
          </div>
        )}
      </Modal>

      <Modal
        visible={confirmingArchive}
        onOk={() => {
          setConfirmingArchive(false);
          handleArchive(selectedItem.id);
        }}
        onCancel={() => setConfirmingArchive(false)}
        okText="Yes"
        cancelText="No"
      >
        <Alert message="Are you sure you want to archive this?" type="warning" showIcon />
      </Modal>
    </Container>
  );
};

export default InappropriateWordLog;
